import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCosmetics } from '../contexts/CosmeticsContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Award,
  Lock,
  Check,
  Loader2,
  Shield,
  Eye,
  Trophy,
  Zap,
  Skull,
  Crown,
  Star,
  Clock,
  TrendingUp,
  Grid3X3,
  Flame,
  Infinity,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RARITY_COLORS = {
  common: { bg: 'bg-slate-500/20', text: 'text-slate-300', border: 'border-slate-500/30' },
  rare: { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30' },
  epic: { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/30' },
  legendary: { bg: 'bg-amber-500/20', text: 'text-amber-300', border: 'border-amber-500/30' },
};

const AchievementsPage = () => {
  const { user, token } = useAuth();
  const { refreshCosmetics } = useCosmetics();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [tab, setTab] = useState('badge');
  const [equipping, setEquipping] = useState(null);
  const [unlockedCount, setUnlockedCount] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchAchievements = async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/achievements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAchievements(data.achievements || []);
        setUnlockedCount(data.unlocked_count || 0);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch achievements:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkAchievements = async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API_URL}/api/achievements/check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.count > 0) {
          toast.success(`Unlocked ${data.count} new achievement${data.count > 1 ? 's' : ''}!`);
          data.newly_granted.forEach(a => {
            toast.success(`Achievement: ${a.name}`, { duration: 5000 });
          });
          fetchAchievements();
          refreshCosmetics();
        } else {
          toast.info('No new achievements unlocked yet. Keep playing!');
        }
      }
    } catch {
      toast.error('Failed to check achievements');
    } finally {
      setChecking(false);
    }
  };

  const equipCosmetic = async (cosmeticId) => {
    setEquipping(cosmeticId);
    try {
      const res = await fetch(`${API_URL}/api/cosmetics/equip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ item_id: cosmeticId })
      });
      if (res.ok) {
        toast.success('Equipped!');
        refreshCosmetics();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to equip');
      }
    } catch {
      toast.error('Failed to equip');
    } finally {
      setEquipping(null);
    }
  };

  useEffect(() => {
    fetchAchievements();
  }, [token]);

  const badges = achievements.filter(a => a.type === 'badge');
  const frames = achievements.filter(a => a.type === 'avatar_frame');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 pt-20 pb-12 px-4">
        <div className="max-w-2xl mx-auto mt-16 text-center">
          <Award className="h-16 w-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Achievements</h2>
          <p className="text-slate-400">Log in to view and earn achievements</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pt-20 pb-12 px-4" data-testid="achievements-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Award className="w-8 h-8 text-amber-400" />
              Achievements
            </h1>
            <p className="text-slate-400 mt-1">
              {unlockedCount} of {total} unlocked — earn rewards through gameplay
            </p>
          </div>
          <Button
            onClick={checkAchievements}
            disabled={checking}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="check-achievements-btn"
          >
            {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Award className="w-4 h-4 mr-2" />}
            Check Progress
          </Button>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">{unlockedCount}/{total} Achievements</span>
            <span className="text-emerald-400 text-sm font-medium">{total > 0 ? Math.round(unlockedCount / total * 100) : 0}%</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full transition-all duration-500"
              style={{ width: `${total > 0 ? (unlockedCount / total * 100) : 0}%` }}
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-800/50 border border-slate-700">
            <TabsTrigger value="badge" data-testid="tab-badges">
              <Shield className="w-4 h-4 mr-2" /> Badges ({badges.filter(b => b.unlocked).length}/{badges.length})
            </TabsTrigger>
            <TabsTrigger value="avatar_frame" data-testid="tab-frames">
              <Crown className="w-4 h-4 mr-2" /> Avatar Frames ({frames.filter(f => f.unlocked).length}/{frames.length})
            </TabsTrigger>
          </TabsList>

          {['badge', 'avatar_frame'].map(type => (
            <TabsContent key={type} value={type} className="mt-6">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {achievements.filter(a => a.type === type).map(ach => {
                  const rarity = RARITY_COLORS[ach.rarity] || RARITY_COLORS.common;
                  return (
                    <Card
                      key={ach.id}
                      className={`border transition-all ${
                        ach.unlocked
                          ? `${rarity.border} bg-slate-800/50 hover:brightness-110`
                          : 'border-slate-800 bg-slate-900/30 opacity-60'
                      }`}
                      data-testid={`achievement-${ach.id}`}
                    >
                      <CardContent className="p-4">
                        {/* Image / Lock */}
                        <div className="relative mb-3 rounded-lg overflow-hidden h-36 bg-slate-900">
                          {ach.image_url ? (
                            <img
                              src={ach.image_url}
                              alt={ach.cosmetic_name}
                              className={`w-full h-full object-cover ${ach.unlocked ? '' : 'grayscale blur-[2px]'}`}
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Award className={`w-12 h-12 ${ach.unlocked ? 'text-amber-400' : 'text-slate-700'}`} />
                            </div>
                          )}
                          {!ach.unlocked && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <Lock className="w-8 h-8 text-slate-400" />
                            </div>
                          )}
                          {ach.unlocked && (
                            <div className="absolute top-2 right-2">
                              <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <h3 className="text-white font-semibold text-sm">{ach.name}</h3>
                            <Badge className={`${rarity.bg} ${rarity.text} text-[10px]`}>{ach.rarity}</Badge>
                          </div>
                          <p className="text-slate-400 text-xs">{ach.description}</p>
                          <p className="text-slate-500 text-[10px]">
                            Unlocks: <span className={ach.unlocked ? 'text-emerald-400' : 'text-slate-400'}>{ach.cosmetic_name}</span>
                          </p>
                        </div>

                        {/* Equip button */}
                        {ach.unlocked && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full mt-3 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                            onClick={() => equipCosmetic(ach.cosmetic_id)}
                            disabled={equipping === ach.cosmetic_id}
                            data-testid={`equip-${ach.cosmetic_id}`}
                          >
                            {equipping === ach.cosmetic_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                            Equip
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default AchievementsPage;
